import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import {
  AUTH_MODE,
  BUYER_INFO_EXTRATION_MODE,
  CART_MODE,
  CONFIRM_SALE_MODE,
  WAITING_CONFIRMATION_MODE,
  PAYMENT_MODE,
  SUCCESSFUL_PAYMENT,
} from '@src/components/Checkout/constants';
import { ConfirmationWait } from '@src/features/orders/awaiting-confirmation';

import { CheckoutAuthStep } from './CheckoutAuthStep';
import { CheckoutAdditionalInfoStep } from './CheckoutAdditionalInfoStep';
import { CheckoutConfirmSaleStep } from './CheckoutConfirmSaleStep';
import { CheckoutPaymentStep } from './CheckoutPaymentStep';
import { CheckoutStepSkeleton } from './CheckoutSkeleton';
import { CheckoutSuccessStep } from './CheckoutSuccessStep';
import type { CheckoutMode } from './types';
import { resolveStepChrome } from './utils';
import { CHECKOUT_ROUTE_PATHS, modeToPath } from './routing';
import { CheckoutCard, CheckoutCardContent } from './styles';

type CheckoutPageOutletProps = {
  mode: CheckoutMode;
  useRouting?: boolean;
  initialMode?: CheckoutMode;
  bootstrapping?: boolean;
};

const STEP_CONTENT_BY_MODE: Record<CheckoutMode, React.ReactNode> = {
  [CART_MODE]: <div>Cart</div>,
  [AUTH_MODE]: <CheckoutAuthStep />,
  [CONFIRM_SALE_MODE]: <CheckoutConfirmSaleStep />,
  [WAITING_CONFIRMATION_MODE]: <ConfirmationWait />,
  [PAYMENT_MODE]: <CheckoutPaymentStep />,
  [BUYER_INFO_EXTRATION_MODE]: <CheckoutAdditionalInfoStep />,
  [SUCCESSFUL_PAYMENT]: <CheckoutSuccessStep />,
};

export const CheckoutPageOutlet = (props: CheckoutPageOutletProps) => {
  const { mode, useRouting = false, initialMode = CART_MODE, bootstrapping = false } = props;

  const { flush } = resolveStepChrome(mode, bootstrapping);

  return (
    <CheckoutCard data-testid='checkout-page-outlet' data-checkout-mode={mode} data-flush={String(flush)}>
      {/* A flush step draws its own padded cards, so the outlet shell adds neither padding nor background. */}
      <CheckoutCardContent data-flush={String(flush)}>
        {bootstrapping ? (
          <CheckoutStepSkeleton />
        ) : useRouting ? (
          <Routes>
            <Route path={CHECKOUT_ROUTE_PATHS[CART_MODE]} element={STEP_CONTENT_BY_MODE[CART_MODE]} />
            <Route path={CHECKOUT_ROUTE_PATHS[AUTH_MODE]} element={STEP_CONTENT_BY_MODE[AUTH_MODE]} />
            <Route path={CHECKOUT_ROUTE_PATHS[CONFIRM_SALE_MODE]} element={STEP_CONTENT_BY_MODE[CONFIRM_SALE_MODE]} />
            <Route
              path={CHECKOUT_ROUTE_PATHS[WAITING_CONFIRMATION_MODE]}
              element={STEP_CONTENT_BY_MODE[WAITING_CONFIRMATION_MODE]}
            />
            <Route path={CHECKOUT_ROUTE_PATHS[PAYMENT_MODE]} element={STEP_CONTENT_BY_MODE[PAYMENT_MODE]} />
            <Route
              path={CHECKOUT_ROUTE_PATHS[BUYER_INFO_EXTRATION_MODE]}
              element={STEP_CONTENT_BY_MODE[BUYER_INFO_EXTRATION_MODE]}
            />
            <Route path={CHECKOUT_ROUTE_PATHS[SUCCESSFUL_PAYMENT]} element={STEP_CONTENT_BY_MODE[SUCCESSFUL_PAYMENT]} />
            <Route path='*' element={<Navigate to={modeToPath(initialMode)} replace />} />
          </Routes>
        ) : (
          STEP_CONTENT_BY_MODE[mode]
        )}
      </CheckoutCardContent>
    </CheckoutCard>
  );
};

export default CheckoutPageOutlet;
